// src/utils/lazyWithRetry.js
//
// Wraps React.lazy() so that when a dynamic import fails because the chunk
// no longer exists on the server (this happens right after a new deploy -
// the visitor's already-open tab still references the *old* build's hashed
// filenames, e.g. "ManageBookings-CdMS_NrV.js", which got deleted when the
// new build replaced it with a new hash), we do ONE automatic full-page
// reload to pick up the fresh index.html + asset manifest, instead of
// showing the user a broken error screen.
//
// A sessionStorage flag prevents an infinite reload loop if the failure is
// caused by something else (e.g. the user is genuinely offline).

import { lazy } from "react";

export function lazyWithRetry(importFn) {
  return lazy(async () => {
    const storageKey = "chunk-retry";

    try {
      const module = await importFn();
      // Successful load - clear any retry flag so future failures can retry again.
      window.sessionStorage.removeItem(storageKey);
      return module;
    } catch (error) {
      const alreadyRetried = window.sessionStorage.getItem(storageKey) === "true";

      const isChunkLoadError =
        /Failed to fetch dynamically imported module/i.test(error?.message || "") ||
        /Importing a module script failed/i.test(error?.message || "") ||
        /error loading dynamically imported module/i.test(error?.message || "");

      if (isChunkLoadError && !alreadyRetried) {
        window.sessionStorage.setItem(storageKey, "true");
        window.location.reload();
        // Return a never-resolving promise so React.lazy doesn't try to
        // render anything else while the reload happens.
        return new Promise(() => {});
      }

      // Either not a chunk-load error, or we already tried reloading once
      // and it's still failing - let it surface normally (error boundary).
      throw error;
    }
  });
}
